import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { updateText } from 'redux/reducers/orders';
import { getActiveOrderId } from 'redux/selectors/app';
import { getChildBirthday, getChildName } from 'redux/selectors/orders';

import { prefixed, specialText, transformWithPrefix } from 'helpers/string';

import IconEdit from 'components/icons/edit';

import cx from 'classnames';
import styles from './styles.scss';

const PT_PX = 25.4 / 72;

@connect(
  state => ({
    orderId: getActiveOrderId(state),
    name: getChildName(state),
    birthday: getChildBirthday(state),
  }),
  {
    updateText,
  },
)
export default class MomentText extends React.PureComponent {
  static contextTypes = {
    canEdit: PropTypes.bool,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      autoScaleFont: 0,
      zoom: false,
    };
  }

  onChange = e => {
    const { orderId, momentId } = this.props;
    this.props.updateText(orderId, momentId, e.currentTarget.value);
  };

  onFocus = () => {
    const { design, text, orderId, momentId, scale } = this.props;
    const removeOnEdit = design.getIn([
      'options',
      'placeholder_text_remove_on_edit',
    ]);

    const pointSize = PT_PX * scale;
    const options = design.getIn(['options', 'font_options']);
    const fontSize = options.get('size') * pointSize;
    const zoomScale = 14 / fontSize;

    this.props.setFocus(true, zoomScale > 1);
    this.setState({
      zoom: true,
    });

    if (removeOnEdit && typeof text === 'undefined') {
      this.props.updateText(orderId, momentId, '');
    }
  };

  onBlur = e => {
    const { orderId, momentId } = this.props;

    const trimmed = e.currentTarget.value.trim();

    this.props.setFocus(false);
    this.setState({
      zoom: false,
    });

    let updateText;
    if (trimmed !== e.currentTarget.value) {
      updateText = trimmed;
    }

    if (e.currentTarget.value === '') {
      this.props.updateText(orderId, momentId, null);
    } else if (updateText) {
      this.props.updateText(orderId, momentId, updateText);
    }
  };

  componentDidUpdate(prevProps) {
    if (this.wrapper.clientHeight < this.sizeCheck.clientHeight - 5) {
      const { orderId, momentId, text = '' } = this.props;
      const { text: prevText = '' } = prevProps;

      if (text !== prevText) {
        this.props.updateText(
          orderId,
          momentId,
          text.substring(0, text.length - 1),
        );
      }
    }

    this.autoScaleFont();
  }

  autoScaleFont() {
    if (this.sizeCheck && this.wordLengthCheck) {
      const sizeCheckWidth = this.sizeCheck.offsetWidth;
      const wordCheckWidth = this.wordLengthCheck.offsetWidth;

      this.setState({
        autoScaleFont: (sizeCheckWidth / wordCheckWidth) * 95,
      });
    }
  }

  componentDidMount() {
    this.autoScaleFont();
  }

  render() {
    const {
      className,
      text,
      design,
      scale,
      name,
      birthday,
      onLeftPage,
      autoSize,
      autoSizeProps,
      style: customStyle,
    } = this.props;
    const { canEdit } = this.context;
    const { zoom, autoScaleFont } = this.state;
    const verticalAlign = design.getIn([
      'options',
      'font_options',
      'vertical_align',
    ]);
    const isSpecialText = design.getIn([
      'options',
      'special_text',
      'special_text',
    ]);

    const wrapperClasses = cx(styles.wrapper, {
      [styles.autoSize]: autoSize,
    });

    const classes = cx(className, styles.text, {
      [styles.disableEditing]: !canEdit || isSpecialText,
      [styles.verticalCenterAlign]: verticalAlign === 'center',
      [styles.verticalBottomAlign]: verticalAlign === 'bottom',
      [styles.allCaps]:
      design.getIn(['options', 'font_options', 'capitalize']) === 'upper',
      [styles.empty]: !text,
    });
    const style = {};
    const wrapperStyle = {
      color: style.color,
    };
    const options = design.getIn(['options', 'font_options']);
    const pointSize = PT_PX * scale;

    const _text = specialText(text, name, birthday, design.getIn(['options']));

    if (options) {
      if (options.get('autoscale') && autoScaleFont) {
        style.fontSize = Math.min(autoScaleFont, options.get('size')) + 'px';
      } else {
        style.fontSize = options.get('size') * pointSize + 'px';
      }

      style.textAlign = options.get('align');
      style.lineHeight = options.get('leading') * pointSize + 'px';
      style.letterSpacing = options.get('tracking') / 1000 + 'em';
      style.color = options.get('color');

      if (options.get('type')) {
        const [family, weight = 'normal', variant = 'normal'] = options
          .get('type', '')
          .split(':');
        style.fontFamily = family;
        style.fontWeight = weight;
        style.fontStyle = variant;
      }
    }

    const dummyText = _text
      .replace(/\n$/, '<br >&nbsp;')
      .replace(/\n/g, '<br >');

    let textareaStyle = {
      ...style,
      ...(autoSize ? autoSizeProps : {}),
    };

    if (zoom && style.fontSize) {
      const fontSize = options.get('size') * pointSize;
      const lineHeight = options.get('leading') * pointSize;
      const zoomScale = 16 / fontSize;

      if (zoomScale > 1) {
        textareaStyle = {
          ...style,
          width: this.wrapper.offsetWidth * zoomScale,
          minHeight: 80,
          fontSize: 16,
          lineHeight: `${lineHeight / fontSize}em`,
          overflow: 'auto',
          marginLeft: onLeftPage ? 0 : -((this.wrapper.offsetWidth * (zoomScale - 1))),
          backgroundColor: 'rgb(245,245,245)',
          color: '#000',
          borderColor: 'transparent',
          padding: '2px 4px',
          ...(autoSize ? autoSizeProps : {}),
        };
      }
    }

    return (
      <div className={wrapperClasses} style={{
        ...customStyle,
        ...(autoSize ? autoSizeProps : {}),
      }} ref={c => (this.wrapper = c)}>
        <div className={classes} style={wrapperStyle}>
          <textarea
            ref={c => (this.textarea = c)}
            readOnly={!canEdit || isSpecialText}
            style={textareaStyle}
            value={_text}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          />
          <div
            ref={c => (this.sizeCheck = c)}
            className={styles.sizeCheck}
            style={{
              ...style,
              ...(autoSize ? autoSizeProps : {}),
              maxHeight: 'none',
            }}
            dangerouslySetInnerHTML={{ __html: dummyText }}
          />
          {(options && options.get('autoscale')) ? <span
            ref={c => (this.wordLengthCheck = c)}
            className={styles.wordLengthCheck}
            style={style}
            dangerouslySetInnerHTML={{ __html: dummyText }}
          /> : null}
          {canEdit && !isSpecialText ? (
            <IconEdit className={styles.icon} width={16} height={16} />
          ) : null}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/moment-text/index.js